import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import {
  Building2, User, Users, Plus, Trash2, Mail, Key,
  CheckCircle2, Edit3, Shield, Copy, AlertTriangle, Globe
} from "lucide-react";

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [agency, setAgency] = useState(null);
  const [team, setTeam] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [editAgency, setEditAgency] = useState(false);
  const [agencyForm, setAgencyForm] = useState({ name: "", phone: "", address: "", website: "", preferred_language: "fr" });
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "agent" });
  const [inviteResult, setInviteResult] = useState(null);
  const [activeTab, setActiveTab] = useState("agency");
  const [newWebhookKey, setNewWebhookKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [a, tm] = await Promise.all([api.get("/agency/profile"), api.get("/agency/team")]);
      setAgency(a.data);
      setTeam(tm.data);
      setAgencyForm({ name: a.data.name || "", phone: a.data.phone || "", address: a.data.address || "", website: a.data.website || "", preferred_language: a.data.preferred_language || "fr" });
    } catch {}
    if (user?.role === "admin") { try { const { data } = await api.get("/agency/email-logs"); setEmailLogs(data); } catch {} }
  };

  const handleAgencyUpdate = async () => {
    try { await api.put("/agency/profile", agencyForm); setEditAgency(false); fetchAll(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/agency/invite", inviteForm); setInviteResult(data); setInviteForm({ email: "", name: "", role: "agent" }); fetchAll(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleRoleChange = async (id, role) => { try { await api.put(`/agency/team/${id}/role`, { role }); fetchAll(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); } };
  const handleRemove = async (id) => { if (!window.confirm(t("settings.remove_confirm"))) return; try { await api.delete(`/agency/team/${id}`); fetchAll(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); } };

  const generateWebhookKey = async () => {
    setKeyLoading(true);
    try { const { data } = await api.post("/agency/webhook-key"); setNewWebhookKey(data.api_key); fetchAll(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    setKeyLoading(false);
  };

  const revokeWebhookKey = async () => {
    if (!window.confirm("Revoke webhook API key?")) return;
    try { await api.delete("/agency/webhook-key"); setNewWebhookKey(null); fetchAll(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const tabs = [
    { id: "agency", label: t("settings.agency_tab"), icon: Building2 },
    { id: "team", label: t("settings.team_tab"), icon: Users },
    { id: "webhook", label: t("settings.webhook_tab"), icon: Key },
    ...(user?.role === "admin" ? [{ id: "emails", label: t("settings.emails_tab"), icon: Mail }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="settings-page">
      <div className="flex gap-0 border-b border-border">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === tab.id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#868E96] hover:text-[#495057]"}`} data-testid={`tab-${tab.id}`}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Agency Tab */}
      {activeTab === "agency" && (
        <div className="max-w-2xl space-y-4">
          <div className="border border-border bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">{t("settings.agency_profile")}</h3>
              {user?.role === "admin" && <button onClick={() => setEditAgency(!editAgency)} className="h-8 px-3 border border-border text-xs font-medium hover:bg-[#F1F3F5] flex items-center gap-1.5 rounded-sm" data-testid="edit-agency-btn"><Edit3 size={12} /> {editAgency ? t("settings.cancel") : t("settings.edit")}</button>}
            </div>
            {editAgency ? (
              <div className="space-y-3">
                <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.name")}</label><input type="text" value={agencyForm.name} onChange={e => setAgencyForm({...agencyForm, name: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="agency-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.phone")}</label><input type="text" value={agencyForm.phone} onChange={e => setAgencyForm({...agencyForm, phone: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="agency-phone-input" /></div>
                  <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.website")}</label><input type="text" value={agencyForm.website} onChange={e => setAgencyForm({...agencyForm, website: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="agency-website-input" /></div>
                </div>
                <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.address")}</label><input type="text" value={agencyForm.address} onChange={e => setAgencyForm({...agencyForm, address: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="agency-address-input" /></div>
                <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.language")}</label>
                  <select value={agencyForm.preferred_language} onChange={e => setAgencyForm({...agencyForm, preferred_language: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="agency-language-select">
                    {SUPPORTED_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
                <button onClick={handleAgencyUpdate} className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="save-agency-btn">{t("settings.save")}</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("settings.name")} value={agency?.name} />
                <Field label={t("settings.plan")} value={agency?.plan || "free"} />
                <Field label={t("settings.phone")} value={agency?.phone} />
                <Field label={t("settings.website")} value={agency?.website} />
                <Field label={t("settings.address")} value={agency?.address} span={2} />
                <Field label={t("settings.language")} value={SUPPORTED_LANGUAGES.find(l => l.code === agency?.preferred_language)?.label || "Français"} />
                <div><label className="text-xs text-[#868E96] block mb-0.5">{t("settings.agency_id")}</label><p className="text-xs font-medium text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{agency?.id || "—"}</p></div>
              </div>
            )}
          </div>
          <div className="border border-border bg-white p-6">
            <div className="flex items-center gap-2 mb-4"><User size={16} className="text-[#002FA7]" /><h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">{t("settings.your_profile")}</h3></div>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("settings.name")} value={user?.name} />
              <Field label={t("common.email")} value={user?.email} />
              <Field label={t("settings.role")} value={user?.role} />
            </div>
          </div>
        </div>
      )}

      {/* Webhook Tab */}
      {activeTab === "webhook" && (
        <div className="max-w-2xl space-y-4">
          <div className="border border-border bg-white p-6">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">{t("settings.webhook_title")}</h3>
            <p className="text-sm text-[#495057] mb-2">{t("settings.webhook_desc")}</p>
            <div className="bg-[#F8F9FA] border border-border p-3 mb-2">
              <code className="text-xs text-[#0A0A0A] break-all" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="webhook-url">POST /api/properties/webhook/{agency?.id || "{agency_id}"}</code>
            </div>
            <p className="text-xs text-[#868E96] mb-1">{t("settings.webhook_required")}</p>
            <p className="text-xs text-[#002FA7] font-medium">{t("settings.webhook_header")}</p>
          </div>

          <div className="border border-border bg-white p-6">
            <div className="flex items-center gap-2 mb-4"><Key size={16} className="text-[#002FA7]" /><h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">{t("settings.webhook_key_title")}</h3></div>

            {newWebhookKey && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle size={14} className="text-[#E67700]" /><span className="text-sm font-medium text-[#E67700]">{t("settings.webhook_key_warning")}</span></div>
                <div className="bg-white border border-border p-3 flex items-center justify-between">
                  <code className="text-xs text-[#0A0A0A] break-all" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="new-webhook-key">{newWebhookKey}</code>
                  <button onClick={() => navigator.clipboard.writeText(newWebhookKey)} className="p-1.5 hover:bg-[#F1F3F5] rounded-sm ml-2 shrink-0"><Copy size={14} className="text-[#002FA7]" /></button>
                </div>
              </div>
            )}

            {agency?.has_webhook_key ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#2B8A3E]" /><span className="text-sm font-medium text-[#2B8A3E]">{t("settings.webhook_key_active")}</span></div>
                {user?.role === "admin" && (
                  <div className="flex gap-2">
                    <button onClick={generateWebhookKey} disabled={keyLoading} className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm disabled:opacity-50" data-testid="rotate-webhook-key">{keyLoading ? "..." : t("settings.webhook_generate")}</button>
                    <button onClick={revokeWebhookKey} className="h-9 px-4 border border-[#C92A2A] text-[#C92A2A] text-sm font-semibold hover:bg-red-50 rounded-sm" data-testid="revoke-webhook-key">{t("settings.webhook_revoke")}</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[#868E96]">{t("settings.webhook_no_key")}</p>
                {user?.role === "admin" && (
                  <button onClick={generateWebhookKey} disabled={keyLoading} className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm disabled:opacity-50" data-testid="generate-webhook-key">{keyLoading ? "..." : t("settings.webhook_generate")}</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Team Tab */}
      {activeTab === "team" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#495057]">{t("settings.team_members", { count: team.length })}</p>
            {user?.role === "admin" && <button onClick={() => { setShowInvite(true); setInviteResult(null); }} className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] flex items-center gap-2 rounded-sm" data-testid="invite-agent-btn"><Plus size={14} /> {t("settings.invite_agent")}</button>}
          </div>
          <div className="border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-[#F8F9FA]">
                <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.name")}</th>
                <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("common.email")}</th>
                <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.role")}</th>
                <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.language")}</th>
                <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.joined")}</th>
                {user?.role === "admin" && <th className="text-right px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]"></th>}
              </tr></thead>
              <tbody>
                {team.map((m, i) => (
                  <tr key={m.id} className="border-b border-border table-row-hover animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }}>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-7 h-7 bg-[#002FA7] rounded-sm flex items-center justify-center text-white text-xs font-bold shrink-0">{m.name?.charAt(0) || "?"}</div><span className="font-medium text-[#0A0A0A]">{m.name}</span></div></td>
                    <td className="px-4 py-3 text-[#495057]">{m.email}</td>
                    <td className="px-4 py-3">
                      {user?.role === "admin" && m.id !== user.id ? (
                        <select value={m.role} onChange={e => handleRoleChange(m.id, e.target.value)} className="h-7 px-2 border border-border text-xs bg-white rounded-sm" data-testid={`role-select-${m.id}`}>
                          <option value="admin">{t("status.admin")}</option><option value="agent">{t("status.agent")}</option>
                        </select>
                      ) : <span className={`status-badge ${m.role === "admin" ? "status-qualified" : "status-new"}`}>{t(`status.${m.role}`)}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#868E96]">{SUPPORTED_LANGUAGES.find(l => l.code === m.preferred_language)?.flag || "FR"}</td>
                    <td className="px-4 py-3 text-xs text-[#868E96]">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}</td>
                    {user?.role === "admin" && <td className="px-4 py-3 text-right">{m.id !== user.id && <button onClick={() => handleRemove(m.id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`remove-member-${m.id}`}><Trash2 size={14} className="text-[#C92A2A]" /></button>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showInvite && (
            <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowInvite(false)}>
              <div className="bg-white border border-border p-6 w-96" onClick={e => e.stopPropagation()} data-testid="invite-modal">
                <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo' }}>{t("settings.invite_title")}</h3>
                {inviteResult ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 border border-green-200 text-sm text-[#2B8A3E]"><CheckCircle2 size={14} className="inline mr-1" /> {t("settings.invite_success")}</div>
                    <div className="bg-[#F8F9FA] border border-border p-3 space-y-1">
                      <p className="text-xs text-[#868E96]">{t("settings.invite_credentials")}</p>
                      <p className="text-sm"><strong>{t("common.email")} :</strong> {inviteResult.email}</p>
                      <p className="text-sm"><strong>{t("common.temp_password")} :</strong> {inviteResult.temp_password}</p>
                    </div>
                    <p className="text-xs text-[#868E96]">{t("settings.invite_share")}</p>
                    <button onClick={() => setShowInvite(false)} className="w-full h-9 border border-border text-sm rounded-sm hover:bg-[#F1F3F5]">{t("settings.done")}</button>
                  </div>
                ) : (
                  <form onSubmit={handleInvite} className="space-y-3">
                    <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.invite_name")} *</label><input type="text" required value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="invite-name-input" /></div>
                    <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.invite_email")} *</label><input type="email" required value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="invite-email-input" /></div>
                    <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">{t("settings.invite_role")}</label><select value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white rounded-sm" data-testid="invite-role-select"><option value="agent">{t("status.agent")}</option><option value="admin">{t("status.admin")}</option></select></div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowInvite(false)} className="h-9 px-4 border border-border text-sm rounded-sm hover:bg-[#F1F3F5]">{t("settings.cancel")}</button>
                      <button type="submit" className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="invite-submit-btn">{t("settings.invite_send")}</button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Email Logs Tab */}
      {activeTab === "emails" && user?.role === "admin" && (
        <div className="border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-[#F8F9FA]">
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.email_to")}</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.email_subject")}</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.email_status")}</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">{t("settings.email_date")}</th>
            </tr></thead>
            <tbody>
              {emailLogs.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-[#868E96]">{t("settings.no_emails")}</td></tr>
              ) : emailLogs.map((log, i) => (
                <tr key={log.id || i} className="border-b border-border table-row-hover">
                  <td className="px-4 py-3 text-[#0A0A0A]">{log.to}</td>
                  <td className="px-4 py-3 text-[#495057] max-w-xs truncate">{log.subject}</td>
                  <td className="px-4 py-3"><span className={`status-badge ${log.status === "sent" ? "status-completed" : log.status === "mock_sent" ? "status-contacted" : "status-lost"}`}>{log.status}</span></td>
                  <td className="px-4 py-3 text-xs text-[#868E96]">{log.created_at ? new Date(log.created_at).toLocaleString() : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, span }) {
  return <div className={span === 2 ? "col-span-2" : ""}><label className="text-xs text-[#868E96] block mb-0.5">{label}</label><p className="text-sm font-medium text-[#0A0A0A]">{value || "—"}</p></div>;
}
